import api from "./client";

// Types pour les plans et subscriptions
export interface PlanLimits {
  price_monthly_cents: number | null;
  display_price?: boolean;
  trial_days?: number;
  projects: number;
  architectures: number;
  deployments_per_month: number;
  pipeline_minutes_per_month: number;
  environments: string[];
  azure_devops_org: string;
  azure_devops_org_url: string;
  features: {
    multicloud: boolean;
    finops_basic: boolean;
    finops_advanced: boolean;
    ci_cd_basic: boolean;
    ci_cd_advanced: boolean;
    templates_shared: boolean;
    templates_private: boolean;
    sso: boolean;
    priority_support: boolean;
    white_label: boolean;
    byoo_azure_devops: boolean;
    multi_tenant_clients: boolean;
  };
}

export interface PublicPlan {
  code: string;
  name: string;
  description: string;
  price_monthly_cents: number | null;
  display_price: boolean;
  features: string[];
  limits: PlanLimits;
}

export interface Subscription {
  id: string;
  organization_id: string;
  plan: 'free' | 'standard' | 'entreprise' | 'business';
  status: 'active' | 'suspended' | 'cancelled' | 'trial_expired' | 'past_due' | 'incomplete';
  stripe_subscription_id?: string;
  stripe_customer_id?: string;
  trial_end?: string;
  cancel_at_period_end?: boolean;
  azure_devops_org?: string;
  azure_devops_project?: string;
  custom_quotas?: any;
  is_enterprise_negotiated?: boolean;
  internal_notes?: string;
  created_at: string;
  updated_at: string;
}

export interface QuotaInfo {
  limit: number;
  used: number;
  remaining: number;
  unlimited: boolean;
  resets_at?: string;
  // Champs spécifiques membres
  addon_price_cents?: number | null;
  additional_seats_used?: number;
  included_seats?: number;
}

export interface UsageQuotas {
  architectures: QuotaInfo;
  projects: QuotaInfo;
  deployments: QuotaInfo;
  pipeline_minutes: QuotaInfo;
  members?: QuotaInfo;
}

export interface TrialInfo {
  days_remaining: number;
  trial_end: string;
  is_expired: boolean;
  is_expiring_soon: boolean;
}

export interface UsageAlert {
  resource: string;
  message: string;
  severity: 'low' | 'medium' | 'high';
  current?: number;
  limit?: number;
}

export interface UsageDashboard {
  subscription: Subscription;
  quotas: UsageQuotas;
  trial?: TrialInfo;
  alerts: UsageAlert[];
  upgrade_suggestion?: {
    recommended_plan: string;
    reason: string;
  };
}

export interface UpgradeRequest {
  plan: 'standard' | 'entreprise' | 'business';
  payment_method_id?: string;
}

export interface DowngradeRequest {
  plan: 'free' | 'standard' | 'entreprise';
}

/**
 * Obtenir tous les plans publics (pour la page Pricing)
 */
export async function getPublicPlans(): Promise<PublicPlan[]> {
  const response = await api.get("/api/subscriptions/plans");
  return response.data;
}

/**
 * Obtenir la subscription actuelle de l'utilisateur
 */
export async function getCurrentSubscription(): Promise<Subscription> {
  const response = await api.get("/api/subscriptions/current");
  // Le backend retourne { subscription: {...}, limits: {...} }
  return response.data.subscription;
}

/**
 * Obtenir le dashboard d'usage (quotas, métriques, alertes)
 */
export async function getUsageDashboard(): Promise<UsageDashboard> {
  const response = await api.get("/api/subscriptions/usage");
  return response.data;
}

export interface UsageHistoryEntry {
  date: string;
  architectures: number;
  deployments: number;
  pipelineMinutes: number;
}

/**
 * Obtenir l'historique journalier d'utilisation
 */
export async function getUsageHistory(days: 7 | 30 | 90 = 30): Promise<UsageHistoryEntry[]> {
  const response = await api.get(`/api/subscriptions/usage-history?days=${days}`);
  return response.data.history;
}

/**
 * Upgrader vers un plan supérieur
 */
export async function upgradeSubscription(data: UpgradeRequest): Promise<Subscription> {
  const response = await api.post("/api/subscriptions/upgrade", data);
  // Le backend retourne { message, subscription: {...} }
  return response.data.subscription;
}

/**
 * Downgrader vers un plan inférieur
 */
export async function downgradeSubscription(data: DowngradeRequest): Promise<Subscription> {
  const response = await api.post("/api/subscriptions/downgrade", data);
  // Le backend retourne { message, subscription: {...} }
  return response.data.subscription;
}

/**
 * Annuler la subscription (passage en FREE)
 */
export async function cancelSubscription(): Promise<Subscription> {
  const response = await api.post("/api/subscriptions/cancel");
  // Le backend retourne { message, subscription: {...} }
  return response.data.subscription;
}

/**
 * Annuler un downgrade programmé
 */
export async function cancelPendingDowngrade(): Promise<{ success: boolean; message: string; current_plan: string }> {
  const response = await api.post("/api/subscriptions/cancel-downgrade");
  return response.data;
}

/**
 * Obtenir les détails d'un downgrade programmé
 */
export async function getPendingDowngrade(): Promise<any> {
  const response = await api.get("/api/subscriptions/pending-downgrade");
  return response.data.pending_downgrade;
}

/**
 * Obtenir le statut de l'organisation (ACTIVE, OVER_QUOTA, etc.)
 */
export async function getOrganizationStatus(): Promise<{
  organization: {
    id: number;
    name: string;
    status: 'ACTIVE' | 'OVER_QUOTA' | 'TRIAL_EXPIRED' | 'SUSPENDED' | 'CANCELLED';
    over_quota_details: any;
    status_updated_at: string;
  };
  subscription: {
    plan: string;
    pending_plan: string | null;
    downgrade_effective_at: string | null;
  };
  usage: {
    projects: number;
    architectures: number;
    deployments_this_month: number;
    pipeline_minutes_this_month: number;
    api_calls_this_month: number;
    users: number;
  };
}> {
  const response = await api.get("/api/subscriptions/organization-status");
  return response.data;
}

/**
 * Forcer la mise à jour du statut de l'organisation
 */
export async function refreshOrganizationStatus(): Promise<{
  message: string;
  previous_status: string;
  new_status: string;
  over_quota_details: any;
}> {
  const response = await api.post("/api/subscriptions/refresh-status");
  return response.data;
}
